import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import {
  TemplateEditor,
  emptyTemplateValues,
  validateTemplate,
  type TemplateEditorValues,
} from '~/components/TemplateEditor/TemplateEditor';
import { useCreateEmailTemplate } from '~/lib/automations-queries';
import { useTenantContext } from '~/lib/tenantContext';
import styles from './_authed.automations.templates_.editor.module.css';

export const Route = createFileRoute('/_authed/automations/templates_/new')({
  component: NewTemplatePage,
});

function NewTemplatePage() {
  const navigate = useNavigate();
  const { tenants, selectedTenantId } = useTenantContext();
  const create = useCreateEmailTemplate();

  // Defaultear al cliente sobre el que se está operando (no al primero de la
  // lista). Para plantillas el usuario puede cambiar a "Global" si quiere.
  const defaultTenant = selectedTenantId ?? tenants[0]?.id ?? '';
  const [values, setValues] = useState<TemplateEditorValues>(() =>
    emptyTemplateValues(defaultTenant),
  );
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setError(null);
    const validationError = validateTemplate(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      const created = await create.mutateAsync({
        ...values,
      });
      // El SDK pide tenant '' como string vacío, no null. Si el form mandó '', el record queda global.
      void navigate({
        to: '/automations/templates/$templateId',
        params: { templateId: created.id },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/automations/templates" className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden /> Plantillas
        </Link>
        <div className={styles.titleRow}>
          <h2 className="h4" style={{ margin: 0 }}>Nueva plantilla</h2>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={create.isPending}
            >
              <Save size={14} aria-hidden />
              {create.isPending ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
        {error && <p className={styles.errorBanner}>{error}</p>}
      </header>

      <TemplateEditor
        mode="create"
        initial={values}
        saving={create.isPending}
        onChange={setValues}
      />
    </div>
  );
}
